import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CandidateData } from './candidateReducer';
import type { ForeignLanguageEntry, Office, ServiceSimple, Uuid } from './types';

interface UserRef {
  uuid: Uuid;
  full_name: string;
  is_deleted?: boolean;
  profile?: { avatar?: string | null };
}

export interface JobApprover {
  uuid?: Uuid;
  is_default: boolean;
  user: UserRef & { roles?: Array<{ name: string }> };
}

export interface JobWorkflowStep {
  uuid: Uuid;
  type: string;
  name?: string;
  status?: string;
  ordering_number?: number;
  assignments_statuses?: Array<{ name: string }>;
}

export interface JobInfo {
  uuid: Uuid;
  name: string;
  status: string;
  service?: ServiceSimple;
  team_lead?: UserRef;
  recruiters?: UserRef[];
  description?: string | null;
  benefits?: string | null;
  openings?: Opening[];
  /* Job responses carry many domain fields; keep open via index signature so
     consumers can read fields beyond the typed ones without `as any` casts. */
  [key: string]: unknown;
}

export interface Opening {
  uuid: Uuid;
  status: string;
  team_lead: UserRef;
  expected: string;
  priority: string;
  level: string;
  custom_level?: string;
  comment?: string;
  job: { uuid: Uuid };
  offices?: Office[];
  foreign_languages?: ForeignLanguageEntry[];
  [key: string]: unknown;
}

export interface JobCandidatesGroup {
  list: unknown[];
  hasMore: boolean;
  count: number;
}

export interface JobInterviewFeedbacks {
  technical: InterviewFeedback[];
  management: InterviewFeedback[];
}

export interface InterviewFeedback {
  uuid?: Uuid;
  comment?: string;
  positive?: boolean;
  english_level?: string;
  level?: string;
  approver: UserRef;
  /* SerializerMethodField bag — additional fields vary by step. */
  [key: string]: unknown;
}

export interface Interview {
  uuid?: Uuid;
  date?: string;
  from_time?: string;
  to_time?: string;
  type?: string;
  link?: string;
  location?: string;
  locations?: Array<{ id: Uuid; name: string }>;
  approvers?: Array<{ uuid: Uuid; full_name: string }>;
  create_google_event?: boolean;
  [key: string]: unknown;
}

export interface Room {
  id: Uuid;
  name: string;
  [key: string]: unknown;
}

export interface ProbationPeriod {
  entry_date?: string;
  probation_period?: number;
  start_date?: string;
  [key: string]: unknown;
}

export interface OfferingCandidate {
  uuid?: Uuid;
  probation_period?: number;
  expected_answer_date?: string;
  comment?: string;
  [key: string]: unknown;
}

export interface TodaysInterview {
  uuid: Uuid;
  interview_start_datetime: string;
  job: Uuid | number;
  step: Uuid | number;
  candidate: { uuid: Uuid; full_name: string };
  [key: string]: unknown;
}

export interface JobScorecardItem {
  label: string;
  value: number;
  id: Uuid;
}

export interface JobScorecard {
  uuid?: Uuid;
  scorecard_items: JobScorecardItem[];
  [key: string]: unknown;
}

export interface ActivityItem {
  created: string;
  event_type?: string;
  type?: string;
  [key: string]: unknown;
}

export interface JobListItem {
  uuid: Uuid;
  name: string;
  status: string;
  service?: ServiceSimple;
  team_lead?: UserRef;
  created?: string;
  updated_at?: string;
}

export interface JobListLiteItem {
  uuid: Uuid;
  name: string;
  status: string;
  candidates_count?: number;
  openings_count?: number;
  team_lead_full_name?: string;
}

export interface OpeningsCount {
  count: number;
  total: number;
  active_openings_count?: number;
  waiting_for_entry_openings_count?: number;
}

export interface HiredCandidate {
  uuid: Uuid;
  full_name_latin?: string;
  full_name_local?: string;
  [key: string]: unknown;
}

export interface TechnologyAreaOption {
  uuid: Uuid;
  name: string;
  is_outdated?: boolean;
}

/* `setUserData` and `updateUserData` carry the candidate response plus a few
   fields injected by `getCandidateStepAssignment`. Modeled as a partial since
   `clearData` empties the object completely. */
export type JobUserData = Partial<CandidateData> & {
  need_sent_decision?: unknown;
  responsible_recruiter?: {
    uuid: Uuid;
    full_name?: string;
    active_role?: number;
    [key: string]: unknown;
  } | null;
  step_status?: string;
  potential_opening?: unknown;
  offer_decision?: unknown;
  cvs?: CandidateData['cvs'];
};

export interface JobState {
  filter: Record<string, unknown>;
  jobData: JobInfo | Record<string, never>;
  openingsList: Opening[];
  openingCandidate: Opening | null;
  steps: JobWorkflowStep[];
  rooms: Room[];
  candidates: Record<string, JobCandidatesGroup>;
  isCandidateExistsOnJob: boolean;
  loader: Record<string, boolean>;
  candidateDataLoading: boolean;
  approversCandidate: JobApprover[];
  approversCandidateLoading: boolean;
  cvs: CandidateData['cvs'];
  userData: JobUserData;
  jobAssignmentId: string;
  scorecard: JobScorecard[];
  todaysInterviews: TodaysInterview[];
  interview: Interview;
  interview_loading: boolean;
  interviewFeedbacks: JobInterviewFeedbacks;
  interviewFeedbacksLoading: boolean;
  offeringCandidate: OfferingCandidate;
  offeringCandidateLoading: boolean;
  offerRecommendation: unknown;
  probationPeriod: ProbationPeriod;
  entryDateLoading: boolean;
  activityList: ActivityItem[];
  activityListLoading: boolean;
  cvsLinks: Array<{ link: string; url: string }>;
  timelineCommentLoading: boolean;
  timelineLoading: boolean;
}

export const initialState: JobState = {
  filter: {},
  jobData: {},
  openingsList: [],
  openingCandidate: null,
  steps: [],
  rooms: [],
  candidates: {},
  isCandidateExistsOnJob: false,
  loader: {},
  candidateDataLoading: false,
  approversCandidate: [],
  approversCandidateLoading: false,
  cvs: [],
  userData: {},
  jobAssignmentId: '',
  scorecard: [],
  todaysInterviews: [],
  interview: {},
  interview_loading: false,
  interviewFeedbacks: {
    technical: [],
    management: [],
  },
  interviewFeedbacksLoading: false,
  offeringCandidate: {},
  offeringCandidateLoading: false,
  offerRecommendation: null,
  probationPeriod: {},
  entryDateLoading: false,
  activityList: [],
  activityListLoading: false,
  cvsLinks: [],
  timelineCommentLoading: false,
  timelineLoading: false,
};

interface SetCandidatesJobPayload {
  status: string;
  results: unknown[];
  clear?: boolean;
  next?: unknown;
  count: number;
}

interface SetCandidatesLoaderPayload {
  status: string;
  value: boolean;
}

interface SetInterviewFeedbacksPayload {
  isTechnical: boolean;
  feedbacks: InterviewFeedback[];
}

const jobSlice = createSlice({
  name: 'job',
  initialState,
  reducers: {
    changeJobFilter(state, { payload }: PayloadAction<Record<string, unknown>>) {
      state.filter = payload;
    },
    setJobInfo(state, { payload }: PayloadAction<JobInfo | Record<string, never>>) {
      state.jobData = payload;
    },
    setOpeningsList(state, { payload }: PayloadAction<Opening[]>) {
      state.openingsList = payload;
    },
    setStepsToJob(state, { payload }: PayloadAction<JobWorkflowStep[]>) {
      state.steps = payload;
    },
    setCandidatesJob(state, { payload }: PayloadAction<SetCandidatesJobPayload>) {
      const filteredCandidates = state.candidates[payload.status]?.list || [];
      const list = payload.clear ? payload.results : [...filteredCandidates, ...payload.results];
      state.candidates[payload.status] = {
        list,
        hasMore: !!payload.next,
        count: payload.count,
      };
    },
    clearCandidatesJob(state) {
      state.candidates = {};
    },
    setCandidatesLoader(state, { payload }: PayloadAction<SetCandidatesLoaderPayload>) {
      state.loader[payload.status] = payload.value;
    },
    setLoadingCandidateData(state, { payload }: PayloadAction<boolean>) {
      state.candidateDataLoading = payload;
    },
    setApproversCandidate(state, { payload }: PayloadAction<JobApprover[]>) {
      state.approversCandidate = payload;
    },
    setApproversCandidateLoading(state, { payload }: PayloadAction<boolean>) {
      state.approversCandidateLoading = payload;
    },
    setCVSCandidate(state, { payload }: PayloadAction<CandidateData['cvs']>) {
      state.cvs = payload;
    },
    setCVSLinks(state, { payload }: PayloadAction<JobState['cvsLinks']>) {
      state.cvsLinks = payload;
    },
    setUserData(state, { payload }: PayloadAction<JobUserData>) {
      state.userData = payload;
    },
    updateUserData(state, { payload }: PayloadAction<Partial<JobUserData>>) {
      state.userData = { ...state.userData, ...payload };
    },
    setJobStepAssignmentId(state, { payload }: PayloadAction<string>) {
      state.jobAssignmentId = payload;
    },
    setScorecardCandidate(state, { payload }: PayloadAction<JobScorecard[]>) {
      state.scorecard = payload;
    },
    setTodaysnterviews(state, { payload }: PayloadAction<TodaysInterview[]>) {
      state.todaysInterviews = payload;
    },
    setInterview(state, { payload }: PayloadAction<Interview>) {
      state.interview = payload;
    },
    setInterviewFeedbacks(state, { payload }: PayloadAction<SetInterviewFeedbacksPayload>) {
      if (payload.isTechnical) {
        state.interviewFeedbacks.technical = payload.feedbacks;
      } else {
        state.interviewFeedbacks.management = payload.feedbacks;
      }
    },
    setInterviewFeedbacksLoading(state, { payload }: PayloadAction<boolean>) {
      state.interviewFeedbacksLoading = payload;
    },
    setInterviewLoading(state, { payload }: PayloadAction<boolean>) {
      state.interview_loading = payload;
    },
    setOfferingCandidate(state, { payload }: PayloadAction<OfferingCandidate>) {
      state.offeringCandidate = payload;
    },
    setOfferingCandidateLoading(state, { payload }: PayloadAction<boolean>) {
      state.offeringCandidateLoading = payload;
    },
    setOfferRecommendation(state, { payload }: PayloadAction<unknown>) {
      state.offerRecommendation = payload;
    },
    setProbationPeriodData(state, { payload }: PayloadAction<ProbationPeriod>) {
      state.probationPeriod = payload;
    },
    setEntryDateLoading(state, { payload }: PayloadAction<boolean>) {
      state.entryDateLoading = payload;
    },
    setOpeningCandidate(state, { payload }: PayloadAction<Opening | null>) {
      state.openingCandidate = payload;
    },
    setActivityList(state, { payload }: PayloadAction<ActivityItem[]>) {
      state.activityList = payload;
    },
    setActivityListLoading(state, { payload }: PayloadAction<boolean>) {
      state.activityListLoading = payload;
    },
    setTimelineCommentLoading(state, { payload }: PayloadAction<boolean>) {
      state.timelineCommentLoading = payload;
    },
    setTimelineLoading(state, { payload }: PayloadAction<boolean>) {
      state.timelineLoading = payload;
    },
    setRoomsOptions(state, { payload }: PayloadAction<Room[]>) {
      state.rooms = payload;
    },
    setCandidateExists(state, { payload }: PayloadAction<boolean>) {
      state.isCandidateExistsOnJob = payload;
    },
    resetJobReducer(_state) {
      _state = initialState;
    },
  },
});

export const {
  changeJobFilter,
  setJobInfo,
  setOpeningsList,
  setStepsToJob,
  setCandidatesJob,
  clearCandidatesJob,
  setCandidatesLoader,
  setLoadingCandidateData,
  setApproversCandidate,
  setApproversCandidateLoading,
  setCVSCandidate,
  setCVSLinks,
  setScorecardCandidate,
  setInterview,
  setTodaysnterviews,
  setInterviewLoading,
  setInterviewFeedbacks,
  setInterviewFeedbacksLoading,
  setOfferingCandidate,
  setOfferingCandidateLoading,
  setOfferRecommendation,
  setProbationPeriodData,
  setEntryDateLoading,
  setOpeningCandidate,
  setActivityList,
  setActivityListLoading,
  setTimelineCommentLoading,
  setTimelineLoading,
  setRoomsOptions,
  setCandidateExists,
  resetJobReducer,
  setUserData,
  updateUserData,
  setJobStepAssignmentId,
} = jobSlice.actions;
export default jobSlice.reducer;
